import { apiRequest } from '../../shared/api/client';

/**
 * Wave picks — mirrors Invetrak.Api.Endpoints.PickWaveEndpoints, over tenant script 078.
 *
 *   GET    /api/v1/pick-waves/candidates      picks:read
 *   GET    /api/v1/pick-waves                 picks:read
 *   POST   /api/v1/pick-waves                 orders:write
 *   GET    /api/v1/pick-waves/{id}            picks:read
 *   GET    /api/v1/pick-waves/{id}/lines      picks:read
 *   GET    /api/v1/pick-waves/{id}/orders     picks:read
 *   POST   /api/v1/pick-waves/{id}/orders/{orderId}/ship   orders:write
 *   DELETE /api/v1/pick-waves/{id}            orders:write
 *
 * **A wave is a pick.** Building one is the exception to "nothing creates a pick" in `api.ts`, and
 * it is safe for the reason that rule exists: a wave can only take an order that is open or a back
 * order, and it moves each one it takes to `in_wave_pick`, which is not eligible. Once built, the
 * wave is walked, picked and closed short through the ORDINARY pick routes in `api.ts` — only
 * building it and reading it as a wave live here.
 *
 * **Quantities are stocking units throughout.** A wave sums one item across several orders, and
 * those orders may have been placed in different units, so there is only one unit the total can
 * honestly be in.
 */

const COOKIE_INIT: RequestInit = { credentials: 'include' };

/** The three ways legacy builds a wave. */
export type WaveMode = 'selected' | 'single_unit' | 'multi_unit';

/** Why an order did not make it into a wave. Null when it did. */
export type WaveSkipReason = 'not_enough_stock' | 'over_max_units';

export interface PickWaveDto {
  pickId: string;
  pickNumber: string;
  warehouseId: string;
  status: string;
  createdAtUtc: string;
  completedAtUtc: string | null;
  pickerUserId: string | null;
  pickStagingLocationId: string | null;
  isShipped: boolean;
  orderCount: number;
  /** How many member orders have already left. A wave is worked through, not closed at once. */
  shippedOrderCount: number;
  itemCount: number;
  unitsRequested: number;
  unitsPicked: number;
}

export interface PickWaveLineDto {
  pickLineId: string;
  lineNumber: number;
  itemId: string;
  itemNumber: string;
  itemDescription: string | null;
  quantityRequested: number;
  quantityPicked: number;
  quantityOutstanding: number;
  uomId: string;
  uomCode: string;
  status: string;
  /** How many of the wave's orders are waiting on this item. */
  orderCount: number;
}

export interface PickWaveOrderDto {
  seq: number;
  salesOrderId: string;
  orderNumber: string;
  status: string;
  customerPo: string | null;
  requestedShipDateUtc: string | null;
  customerId: string;
  customerNumber: string;
  companyName: string | null;
  previousStatus: string;
  /** Null until this order has shipped out of the wave. */
  shippedAtUtc: string | null;
  shippedByUserId: string | null;
  /**
   * The carrier for THIS order.
   *
   * Per order, not per wave: the orders on one wave go to different customers, very likely by
   * different carriers, on different days.
   */
  shipMethodId: string | null;
  shipMethodName: string | null;
  trackingNumber: string | null;
  billOfLading: string | null;
  quantity: number;
}

export interface PickWaveCandidateDto {
  salesOrderId: string;
  orderNumber: string;
  status: string;
  orderDateUtc: string;
  requestedShipDateUtc: string | null;
  customerId: string;
  customerNumber: string;
  companyName: string | null;
  customerPo: string | null;
  units: number;
  lineCount: number;
  /**
   * Whether the warehouse could cover this order ON ITS OWN, today.
   *
   * Advisory, and it does not account for the other orders on the screen: two orders can each be
   * coverable and only one of them fit. The verdict that counts comes back from building the wave.
   */
  canCover: boolean;
}

export interface PickWaveVerdictDto {
  salesOrderId: string;
  orderNumber: string;
  units: number;
  admitted: boolean;
  reason: WaveSkipReason | null;
}

export interface PickWaveCreatedDto {
  pickID: string;
  orders: PickWaveVerdictDto[];
}

export interface CreateWaveBody {
  mode: WaveMode;
  salesOrderIds?: string[];
  maxUnits?: number;
}

export async function listWaveCandidates(signal?: AbortSignal): Promise<PickWaveCandidateDto[]> {
  const { data } = await apiRequest<PickWaveCandidateDto[]>('/v1/pick-waves/candidates', {
    method: 'GET', signal, ...COOKIE_INIT,
  });
  return data;
}

export async function listWaves(status?: string, signal?: AbortSignal): Promise<PickWaveDto[]> {
  const query = status ? `?status=${encodeURIComponent(status)}` : '';
  const { data } = await apiRequest<PickWaveDto[]>(`/v1/pick-waves${query}`, {
    method: 'GET', signal, ...COOKIE_INIT,
  });
  return data;
}

export async function getWave(pickId: string, signal?: AbortSignal): Promise<PickWaveDto> {
  const { data } = await apiRequest<PickWaveDto>(`/v1/pick-waves/${encodeURIComponent(pickId)}`, {
    method: 'GET', signal, ...COOKIE_INIT,
  });
  return data;
}

export async function listWaveLines(pickId: string, signal?: AbortSignal): Promise<PickWaveLineDto[]> {
  const { data } = await apiRequest<PickWaveLineDto[]>(
    `/v1/pick-waves/${encodeURIComponent(pickId)}/lines`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/**
 * The wave's member orders, or — with an item — only those waiting on that item.
 *
 * The second form is what turns a tote of forty pieces back into the orders it has to be split
 * across, which is the question the packing bench actually asks.
 */
export async function listWaveOrders(
  pickId: string,
  itemId?: string,
  signal?: AbortSignal,
): Promise<PickWaveOrderDto[]> {
  const query = itemId ? `?itemId=${encodeURIComponent(itemId)}` : '';
  const { data } = await apiRequest<PickWaveOrderDto[]>(
    `/v1/pick-waves/${encodeURIComponent(pickId)}/orders${query}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function createWave(body: CreateWaveBody): Promise<PickWaveCreatedDto> {
  const { data } = await apiRequest<PickWaveCreatedDto>('/v1/pick-waves', {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
    ...COOKIE_INIT,
  });
  return data;
}

export interface ShipWaveOrderBody {
  shipMethodId?: string | null;
  trackingNumber?: string | null;
  billOfLading?: string | null;
  shippedAtUtc?: string | null;
}

/**
 * Ship ONE member order out of the wave.
 *
 * A wave is picked once and shipped many times. The order takes what it is still owed, capped by
 * what is left of each item on the dock; short is ordinary and turns it into a back order. Returns
 * the wave's order list, so the screen sees what has gone and what is still to go.
 */
export async function shipWaveOrder(
  pickId: string,
  salesOrderId: string,
  body: ShipWaveOrderBody,
): Promise<PickWaveOrderDto[]> {
  const { data } = await apiRequest<PickWaveOrderDto[]>(
    `/v1/pick-waves/${encodeURIComponent(pickId)}/orders/${encodeURIComponent(salesOrderId)}/ship`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json' },
      ...COOKIE_INIT,
    },
  );
  return data;
}

export async function removeWave(pickId: string): Promise<void> {
  await apiRequest<void>(`/v1/pick-waves/${encodeURIComponent(pickId)}`, {
    method: 'DELETE', ...COOKIE_INIT,
  });
}
